import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, Check, X, User, Scissors, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { respondOvertimeRequest } from '@/services/queueService';
import { OvertimeRejectionReason, OVERTIME_REJECTION_REASON_LABELS } from '@/types';
import type { QueueTicket } from '@/types';
import { toast } from 'sonner';

interface OvertimeRequestCardProps {
  ticket: QueueTicket;
  onResponse?: () => void;
}

export function OvertimeRequestCard({ ticket, onResponse }: OvertimeRequestCardProps) {
  const { t } = useTranslation();
  const [isResponding, setIsResponding] = useState(false);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState<OvertimeRejectionReason | ''>('');
  const [rejectionNotes, setRejectionNotes] = useState('');
  const [timeRemaining, setTimeRemaining] = useState<number>(0);

  const overtimeRequest = ticket.overtimeRequest;

  // Desfase entre el reloj del dispositivo y el del servidor, calculado una
  // sola vez con requestedAt. Sin esto, un móvil con la hora adelantada hacía
  // desaparecer la tarjeta al instante y el barbero nunca veía la solicitud.
  const clockOffsetRef = useRef<number | null>(null);

  const toMillis = (value: unknown): number | null => {
    if (value instanceof Date) return value.getTime();
    const ts = value as { toMillis?: () => number } | null;
    return typeof ts?.toMillis === 'function' ? ts.toMillis() : null;
  };

  // Cuenta atrás: solo informativa. No oculta ni caduca la solicitud; de eso
  // se encarga el servidor (la suscripción sólo trae las que siguen pendientes)
  useEffect(() => {
    if (!overtimeRequest?.expiresAt) return;

    const expiresAtMs = toMillis(overtimeRequest.expiresAt);
    if (expiresAtMs === null) return;

    if (clockOffsetRef.current === null) {
      const requestedAtMs = toMillis(overtimeRequest.requestedAt);
      clockOffsetRef.current = requestedAtMs === null ? 0 : Date.now() - requestedAtMs;
    }

    const updateTimer = () => {
      const serverNow = Date.now() - (clockOffsetRef.current ?? 0);
      setTimeRemaining(Math.max(0, Math.floor((expiresAtMs - serverNow) / 1000)));
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [overtimeRequest?.expiresAt, overtimeRequest?.requestedAt]);

  // Format time as HH:MM:SS or MM:SS
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const handleAccept = async () => {
    setIsResponding(true);
    try {
      await respondOvertimeRequest({
        queueId: ticket.queueId,
        accept: true,
      });
      toast.success(t('barber.overtime.acceptedSuccess'));
      onResponse?.();
    } catch (error) {
      console.error('Error accepting overtime:', error);
      toast.error(t('barber.overtime.acceptError'));
    } finally {
      setIsResponding(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason) {
      toast.error(t('barber.overtime.selectReason'));
      return;
    }

    setIsResponding(true);
    try {
      await respondOvertimeRequest({
        queueId: ticket.queueId,
        accept: false,
        reason: rejectionReason as OvertimeRejectionReason,
        notes: rejectionNotes || undefined,
      });
      toast.success(t('barber.overtime.rejectedSuccess'));
      onResponse?.();
    } catch (error) {
      console.error('Error rejecting overtime:', error);
      toast.error(t('barber.overtime.rejectError'));
    } finally {
      setIsResponding(false);
    }
  };

  // Sólo se oculta si el servidor dice que ya no hay solicitud pendiente
  if (!overtimeRequest) return null;

  return (
    <div className="bg-surface border border-warning/40 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 flex items-center justify-between border-b border-hairline">
        <div className="flex items-center gap-2 text-warning">
          <AlertTriangle className="w-5 h-5" aria-hidden="true" />
          <span className="font-semibold">{t('barber.overtime.newRequest')}</span>
        </div>
        <div className="flex items-center gap-2 bg-surface-2 border border-hairline rounded-full px-3 py-1">
          <Clock className="w-4 h-4 text-ink-subtle" aria-hidden="true" />
          <span className="font-mono font-semibold text-ink tabular-nums">{formatTime(timeRemaining)}</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Client Info */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-surface-3 flex items-center justify-center">
            <User className="w-6 h-6 text-ink-subtle" aria-hidden="true" />
          </div>
          <div>
            <p className="font-semibold text-ink text-lg">
              {overtimeRequest.userName}
            </p>
            <p className="text-sm text-ink-muted">
              {t('barber.overtime.requestsOvertime')}
            </p>
          </div>
        </div>

        {/* Service Info */}
        <div className="bg-surface-2 border border-hairline rounded-xl p-3 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Scissors className="w-4 h-4 text-ink-subtle" aria-hidden="true" />
            <span className="text-sm font-medium text-ink">
              {overtimeRequest.serviceName}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-ink-muted">
              {t('barber.overtime.yourShiftEnds')}:
            </span>
            <span className="font-medium text-ink tabular-nums">
              {overtimeRequest.barberWorkEnd}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-ink-muted">
              {t('barber.overtime.serviceEnds')}:
            </span>
            <span className="font-medium text-warning tabular-nums">
              {overtimeRequest.estimatedServiceEnd}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm mt-1 pt-1 border-t border-hairline">
            <span className="text-ink-muted">
              {t('barber.overtime.extraTime')}:
            </span>
            <span className="font-bold text-warning tabular-nums">
              +{overtimeRequest.overtimeMinutes} min
            </span>
          </div>
        </div>

        {/* Reject Form */}
        {showRejectForm ? (
          <div className="space-y-3">
            <p className="text-sm font-medium text-ink-muted">
              {t('barber.overtime.selectReasonLabel')}:
            </p>
            <div className="space-y-2">
              {Object.entries(OVERTIME_REJECTION_REASON_LABELS).map(([key, label]) => (
                <label
                  key={key}
                  className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                    rejectionReason === key
                      ? 'border-danger/50 bg-danger/10'
                      : 'border-hairline hover:border-hairline-strong'
                  }`}
                >
                  <input
                    type="radio"
                    name="rejectionReason"
                    value={key}
                    checked={rejectionReason === key}
                    onChange={(e) => setRejectionReason(e.target.value as OvertimeRejectionReason)}
                    className="sr-only"
                  />
                  <span className="text-sm text-ink">{label}</span>
                </label>
              ))}
            </div>

            <textarea
              value={rejectionNotes}
              onChange={(e) => setRejectionNotes(e.target.value)}
              placeholder={t('barber.overtime.notesPlaceholder')}
              maxLength={200}
              className="w-full p-3 rounded-lg border border-hairline bg-surface-3 text-ink placeholder:text-ink-subtle text-sm resize-none focus:ring-2 focus:ring-focus focus:border-transparent"
              rows={2}
            />

            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={() => setShowRejectForm(false)}
                className="flex-1"
                disabled={isResponding}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="danger"
                onClick={handleReject}
                isLoading={isResponding}
                disabled={!rejectionReason || isResponding}
                className="flex-1"
              >
                {t('barber.overtime.confirmReject')}
              </Button>
            </div>
          </div>
        ) : (
          /* Action Buttons */
          <div className="flex gap-3">
            <Button
              variant="ghost"
              onClick={() => setShowRejectForm(true)}
              disabled={isResponding}
              className="flex-1 text-danger hover:bg-danger/20 border border-hairline"
            >
              <X className="w-4 h-4 mr-2" />
              {t('barber.overtime.reject')}
            </Button>
            <Button
              variant="primary"
              onClick={handleAccept}
              isLoading={isResponding}
              disabled={isResponding}
              className="flex-1"
            >
              <Check className="w-4 h-4 mr-2" />
              {t('barber.overtime.accept')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
